import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { InputWithError } from "../components/common/InputWithError";
import { Loader2, Pencil, Trash2 } from "lucide-react";

export function AssetFormDialog({
  open, onClose, editingId, form, setForm, onSubmit, saving,
  tipologie, centriCosto, assetErrors, validateAssetField, t
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{editingId ? t("assets.editAsset") : t("assets.newAsset")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("assets.assetType")} *</Label>
              <Select value={form.tipologia} onValueChange={(v) => setForm({ ...form, tipologia: v })}>
                <SelectTrigger data-testid="select-tipologia" className={assetErrors.tipologia ? "border-destructive" : ""}>
                  <SelectValue placeholder={t("dialogs.selectType")} />
                </SelectTrigger>
                <SelectContent>
                  {tipologie.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      {t("dialogs.noTypeDefined")}
                    </div>
                  ) : (
                    tipologie.map(tp => (
                      <SelectItem key={tp.id} value={tp.nome}>{tp.nome}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {assetErrors.tipologia && <p className="text-xs text-destructive">{assetErrors.tipologia}</p>}
            </div>
            <div className="space-y-2">
              <Label>{t("dialogs.internalAssetN")}</Label>
              <Input value={form.n_asset_interno} onChange={(e) => setForm({ ...form, n_asset_interno: e.target.value })} placeholder={t("dialogs.exampleAssetCode")} data-testid="input-n-asset" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('vehicles.brand')} *</Label>
              <InputWithError value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} onValidate={() => validateAssetField('marca', form.marca)} placeholder={t("dialogs.exampleBrand")} error={assetErrors.marca} data-testid="input-marca" />
            </div>
            <div className="space-y-2">
              <Label>{t('vehicles.model')} *</Label>
              <InputWithError value={form.modello} onChange={(e) => setForm({ ...form, modello: e.target.value })} onValidate={() => validateAssetField('modello', form.modello)} placeholder={t("dialogs.exampleModel")} error={assetErrors.modello} data-testid="input-modello" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("dialogs.assetSerialNumber")}</Label>
              <Input value={form.matricola} onChange={(e) => setForm({ ...form, matricola: e.target.value })} placeholder={t("dialogs.exampleSerial")} data-testid="input-matricola" />
            </div>
            <div className="space-y-2">
              <Label>{t('common.supplier')}</Label>
              <Input value={form.fornitore} onChange={(e) => setForm({ ...form, fornitore: e.target.value })} placeholder={t("dialogs.exampleSupplier")} data-testid="input-fornitore" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("dialogs.purchaseDate")}</Label>
              <DateInput value={form.data_acquisto} onChange={(e) => setForm({ ...form, data_acquisto: e.target.value })} data-testid="input-data-acquisto" />
            </div>
            <div className="space-y-2">
              <Label>{t("dialogs.invoiceRef")}</Label>
              <Input value={form.fattura_riferimento} onChange={(e) => setForm({ ...form, fattura_riferimento: e.target.value })} placeholder={t("dialogs.exampleInvoice")} data-testid="input-fattura" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("dialogs.calibrationExpiry")}</Label>
              <DateInput value={form.scadenza_taratura} onChange={(e) => setForm({ ...form, scadenza_taratura: e.target.value })} data-testid="input-scadenza-taratura" />
            </div>
            <div className="space-y-2">
              <Label>{t("common.status")}</Label>
              <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                <SelectTrigger data-testid="select-stato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operativa">{t("dialogs.operational")}</SelectItem>
                  <SelectItem value="in_manutenzione">{t("dialogs.inMaintenance")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("dialogs.currentLocation")}</Label>
            <SearchableSelect
              options={[{ value: "__none__", label: t("dialogs.none") }, ...centriCosto.map(cc => ({ value: cc.id, label: cc.nome }))]}
              value={form.centro_costo_id} onValueChange={(v) => setForm({ ...form, centro_costo_id: v })}
              placeholder={t("dialogs.selectLocation")} searchPlaceholder={t("dialogs.searchCostCenter")} emptyMessage={t("dialogs.noCostCenterFound")} testId="select-centro-costo"
            />
          </div>
          <div className="space-y-2">
            <Label>{t("common.notes")}</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("common.notes")} data-testid="input-note" className="resize-none" rows={3} />
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={onSubmit} data-testid="save-btn" disabled={saving}>
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : (editingId ? t("common.save") : t("common.create"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AssetDeleteDialog({ open, item, onClose, onDelete, deleting, t }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
          <DialogDescription>
            Eliminare l&apos;asset {item?.marca} {item?.modello}? Verranno eliminati anche tutti gli spostamenti registrati.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Annulla</Button>
          <Button variant="destructive" onClick={onDelete} data-testid="confirm-delete-btn" disabled={deleting}>
            {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Eliminazione...</> : 'Elimina'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AssetTipologieDialog({
  open, onClose, tipologie, tipologiaForm, setTipologiaForm,
  editingTipologia, setEditingTipologia, onAdd, onDelete, t
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{t("assets.manageTypes")}</DialogTitle>
          <DialogDescription>{t("dialogs.manageTypesShort")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 overflow-y-auto flex-1">
          <div className="space-y-3 p-3 bg-muted rounded-lg">
            <div className="space-y-2">
              <Label>{t("dialogs.typeName")} *</Label>
              <Input value={tipologiaForm.nome} onChange={(e) => setTipologiaForm({ ...tipologiaForm, nome: e.target.value })} placeholder={t("dialogs.exampleAsset")} data-testid="input-tipologia-nome" />
            </div>
            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Input value={tipologiaForm.descrizione} onChange={(e) => setTipologiaForm({ ...tipologiaForm, descrizione: e.target.value })} placeholder={t("common.optional")} data-testid="input-tipologia-desc" />
            </div>
            <div className="flex gap-2">
              <Button onClick={onAdd} size="sm">{editingTipologia ? t("common.update") : t("common.add")}</Button>
              {editingTipologia && (
                <Button variant="outline" size="sm" onClick={() => { setEditingTipologia(null); setTipologiaForm({ nome: "", descrizione: "" }); }}>{t("common.cancel")}</Button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("dialogs.existingTypes")}</Label>
            {tipologie.length === 0 ? (
              <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded">{t("dialogs.noTypeDefinedShort")}</p>
            ) : (
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {tipologie.map(tip => (
                  <div key={tip.id} className="flex items-center justify-between p-2 bg-background border rounded">
                    <div className="truncate flex-1 mr-2">
                      <span className="font-medium">{tip.nome}</span>
                      {tip.descrizione && <span className="text-xs text-muted-foreground ml-2 truncate">- {tip.descrizione}</span>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingTipologia(tip); setTipologiaForm({ nome: tip.nome, descrizione: tip.descrizione || "" }); }}><Pencil className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDelete(tip)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <Button variant="outline" onClick={onClose}>{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
